import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Box, makeStyles } from "@material-ui/core";
import { fetchCases, fetchCaseStats, PAGE_SIZE } from "./reviewApi";
import Inbox from "./Inbox";
import { filterParams, filterQuery, parseFilters } from "./Filters";
import CaseView from "./CaseView";
import { PANEL_BG, errMsg, pal } from "./reviewUi";

// AGN-206 Spam Review: split view. Left, the inbox of flagged projects
// ("cases"); right, the selected case. The case lives in the URL as
// ?case=<assistantId> so it can be shared and survives back / refresh; the
// Pending sub-tab (Blocked / Review / Uncertain) lives there too as ?sev=,
// and so do the violation / date filters (see Filters.js).

const useStyles = makeStyles((theme) => ({
  root: {
    display: "grid",
    gridTemplateColumns: "360px minmax(0, 1fr)",
    // ProjectsLayout's route container is 100vh + overflow hidden: each pane scrolls itself.
    height: "100%",
    width: "100%",
    background: "#fff",
    color: pal(theme).text,
    fontSize: 13,
    lineHeight: 1.5,
    "@media (max-width: 1180px)": { gridTemplateColumns: "320px minmax(0, 1fr)" },
  },
  inbox: {
    background: PANEL_BG,
    borderRight: `1px solid ${pal(theme).line}`,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  pane: { minHeight: 0, overflowY: "auto", background: "#fff" },
  empty: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: pal(theme).hint,
    fontSize: 13,
  },
}));

const EMPTY_LIST = { cases: [], total: 0, limit: PAGE_SIZE };

// Pending sub-tabs -> backend severity filter.
const SEVERITY = { blocked: "flagged", review: "review", uncertain: "uncertain" };
// Older backends have no severity filter: their case-stats lack these counts.
const hasSeverity = (stats) => !!stats && stats.flagged != null && stats.uncertain != null;
// Backends without the Review sub-tab lack its count ("flagged" is then block + review).
const hasReview = (stats) => hasSeverity(stats) && stats.review != null;
// Which list a response belongs to: severity + filter params (null on Suspended).
const listKey = (severity, params) => JSON.stringify([severity, params || null]);

// Exact id search: the box takes a project id or an owner clientId, so both are tried.
const searchCases = async (tab, subjectType, q) => {
  const [byProject, byOwner] = await Promise.all([
    fetchCases({ tab, subjectType, assistantId: q, limit: 50 }),
    fetchCases({ tab, subjectType, clientId: q, limit: 50 }),
  ]);
  const seen = new Set();
  const cases = [...(byProject.cases || []), ...(byOwner.cases || [])].filter((c) => {
    if (seen.has(c.assistantId)) return false;
    seen.add(c.assistantId);
    return true;
  });
  return { cases, total: cases.length, limit: 50 };
};

export default function SpamReviewPage() {
  const c = useStyles();
  const history = useHistory();
  const location = useLocation();
  const permissions = useSelector((state) => state.login.permissions);
  const canWrite = !!permissions?.find((p) => p.label === "spam-review")?.write;
  // Owner login: same gate as the Assistants page (generate-any-token checks it too).
  const canLogin = !!permissions?.find((p) => p.label === "assistants")?.actions?.includes("login");

  const [tab, setTab] = useState("pending");
  const [subject, setSubject] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [tick, setTick] = useState(0);
  const [list, setList] = useState(EMPTY_LIST);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const reqRef = useRef(0);

  const urlParams = new URLSearchParams(location.search);
  const caseId = urlParams.get("case") || "";
  const sevParam = urlParams.get("sev");
  const sev = sevParam === "uncertain" || (sevParam === "review" && hasReview(stats)) ? sevParam : "blocked";
  const subjectType = tab === "suspended" ? "" : subject;
  // Wait for stats on Pending so an old backend never gets a severity param.
  const sevReady = tab !== "pending" || stats !== null;
  const severity = tab === "pending" && hasSeverity(stats) ? SEVERITY[sev] : "";
  // Violation / date filters: not on Suspended (the backend ignores them there).
  const filters = parseFilters(urlParams);
  const active = tab === "suspended" ? null : filterParams(filters);
  const { label, policy, date, from, to } = active || {};
  const clearIfMissing = useRef(""); // list key whose list decides whether the open case stays

  useEffect(() => {
    if (!sevReady) return;
    const req = ++reqRef.current;
    setLoading(true);
    setError("");
    // Exact-id search spans all of the tab, whatever the sub-tab.
    const run = query
      ? searchCases(tab, subjectType, query)
      : fetchCases({ tab, subjectType, severity, ...filterQuery({ label, policy, date, from, to }), page });
    run
      .then((res) => {
        if (req !== reqRef.current) return;
        const key = query ? null : listKey(severity, tab === "suspended" ? null : { label, policy, date, from, to });
        setList({ cases: res.cases || [], total: res.total || 0, limit: res.limit || PAGE_SIZE, key });
      })
      .catch((e) => {
        if (req !== reqRef.current) return;
        setError(errMsg(e, "Couldn't load accounts"));
        setList(EMPTY_LIST);
      })
      .finally(() => {
        if (req === reqRef.current) setLoading(false);
      });
  }, [tab, subjectType, severity, label, policy, date, from, to, sevReady, query, page, tick]);

  // Header stats; older backends lack /case-stats, so fall back to list totals (no high risk).
  useEffect(() => {
    let alive = true;
    fetchCaseStats()
      .catch(() => {
        const total = (t) =>
          fetchCases({ tab: t, page: 1, limit: 1 })
            .then((r) => r.total || 0)
            .catch(() => null);
        return Promise.all([total("pending"), total("suspended")]).then(([pending, suspended]) => ({
          pending,
          suspended,
          highRisk: null,
        }));
      })
      .then((s) => {
        if (alive) setStats(s);
      });
    return () => {
      alive = false;
    };
  }, [tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  const setParams = useCallback(
    (changes, replace) => {
      const params = new URLSearchParams(location.search);
      Object.entries(changes).forEach(([k, v]) => (v ? params.set(k, v) : params.delete(k)));
      const qs = params.toString();
      history[replace ? "replace" : "push"]({ pathname: location.pathname, search: qs ? `?${qs}` : "" });
    },
    [history, location.pathname, location.search]
  );
  const openCase = useCallback((id) => setParams({ case: id }), [setParams]);

  // After a sub-tab or filter switch, drop the open case if the new list doesn't have it.
  useEffect(() => {
    if (!clearIfMissing.current || loading || list.key !== clearIfMissing.current) return;
    clearIfMissing.current = "";
    if (caseId && !list.cases.some((r) => r.assistantId === caseId)) setParams({ case: "" }, true);
  }, [list, loading, caseId, setParams]);

  return (
    <Box className={c.root}>
      <Box className={c.inbox}>
        <Inbox
          tab={tab}
          onTab={(t) => {
            setTab(t);
            setPage(1);
          }}
          sev={hasSeverity(stats) ? sev : ""}
          onSev={(v) => {
            if (v === sev) return;
            clearIfMissing.current = listKey(SEVERITY[v], active);
            setPage(1);
            setParams({ sev: v });
          }}
          filters={filters}
          facets={stats?.facets || null}
          onFilters={(f) => {
            const changes = filterParams(f);
            if (listKey(severity, changes) === listKey(severity, active)) return;
            clearIfMissing.current = listKey(severity, changes);
            setPage(1);
            setParams(changes);
          }}
          subject={subject}
          onSubject={(s) => {
            setSubject(s);
            setPage(1);
          }}
          query={query}
          onQuery={(q) => {
            setQuery(q);
            setPage(1);
          }}
          stats={stats}
          list={list}
          page={page}
          onPage={setPage}
          loading={loading}
          error={error}
          onRetry={reload}
          selectedId={caseId}
          onOpen={openCase}
        />
      </Box>
      <Box className={c.pane}>
        {caseId ? (
          <CaseView key={caseId} assistantId={caseId} canWrite={canWrite} canLogin={canLogin} onOpenCase={openCase} onChanged={reload} />
        ) : (
          <div className={c.empty}>Select an account to review</div>
        )}
      </Box>
    </Box>
  );
}
